import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { PictureItemType } from './PictureEditDrawer';
import { styled } from '@mui/material';
import { CustomPopover } from '../../../../../ui/CustomPopover';
import { BrokenImageOutlined, DeleteOutline, FileDownloadOutlined, MoreHoriz } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql } from '../../../../../gql';
import { useApiMutation } from '../../../../../query/hooks';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useSnackbar } from '../../../../../store/snackbar';
import { getNightOrDayPalette } from '../../../../../utils/getNightOrDayPalette';

type Props = Readonly<{
	picture: Partial<PictureItemType>;
	isProcessed?: boolean;
	sortable?: boolean;
}>;

const deletePictureMutation = graphql(`
	mutation pictureItem_deleteFromActivity($pictureId: UUID!) {
		deletePicture(by: { id: $pictureId }) {
			ok
			errorMessage
		}
	}
`);

const Picture = styled('picture')`
	border-radius: 4px;
	aspect-ratio: 3/2;
	width: 100%;
	height: 100%;
	overflow: hidden;
	display: block;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		overflow: hidden;
	}
`;

export const PictureItem: FC<Props> = ({ picture, isProcessed = false, sortable = false }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { mutate: deletePicture } = useApiMutation(deletePictureMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.deletePicture.ok,
				toastMessages: { error: intl.formatMessage({ id: 'widgets.pictures.delete.error' }) },
				errorMessage: result.deletePicture.errorMessage,
				openSnackbar,
			}),
	});

	return picture.image?.url ? (
		<Box position={'relative'} width={'100%'} borderRadius={1} overflow={'hidden'}>
			{isProcessed && (
				<LinearProgress
					sx={{
						position: 'absolute',
						top: 0,
						width: '100%',
						zIndex: (theme) => theme.zIndex.drawer + 1,
						borderBottomLeftRadius: 0,
						borderBottomRightRadius: 0,
					}}
				/>
			)}
			{!isProcessed && (
				<Button
					sx={{
						position: 'absolute',
						top: 0,
						right: 0,
						margin: 1,
						zIndex: 1,
						p: 0,
						minWidth: 0,
						bgcolor: (theme) => theme.palette[getNightOrDayPalette(theme)][50],

						':hover': {
							bgcolor: (theme) => theme.palette[getNightOrDayPalette(theme)][100],
						},
					}}
					onClick={(e) => e.stopPropagation()}
				>
					<CustomPopover
						layoutIcon={<MoreHoriz />}
						closeOnAction
						options={[
							{
								icon: <FileDownloadOutlined sx={{ fontSize: 18, fill: (theme) => theme.palette.grey[500] }} />,
								caption: <FormattedMessage id={'common.download'} />,
								onAction: () => {
									if (picture.image && picture.image.url) {
										fetch(picture.image.url, { method: 'get', referrerPolicy: 'no-referrer' })
											.then((res) => res.blob())
											.then((res) => {
												const aElement = document.createElement('a');
												aElement.setAttribute('download', picture.id ?? 'image');
												const href = URL.createObjectURL(res);
												aElement.setAttribute('href', href);
												aElement.setAttribute('target', '_blank');
												aElement.click();
												URL.revokeObjectURL(href);
											});
									}
								},
							},
							{
								icon: <DeleteOutline sx={{ fontSize: 18, fill: (theme) => theme.palette.error.main }} />,
								caption: (
									<Typography color={(theme) => theme.palette.error.main}>
										<FormattedMessage id={'common.delete'} />
									</Typography>
								),
								onAction: () => picture.id && deletePicture({ pictureId: picture.id }),
							},
						]}
					/>
				</Button>
			)}
			<Picture
				sx={{
					opacity: isProcessed ? '0.7' : '1',
					cursor: sortable ? 'grab' : 'initial',
					'&:active': {
						cursor: sortable ? 'grabbing' : 'initial',
					},
				}}
			>
				<img src={picture.image.url} alt={picture.id} />
			</Picture>
		</Box>
	) : (
		<Box position={'relative'} borderRadius={1} overflow={'hidden'}>
			<Box position={'absolute'} right={0} top={0} margin={1} zIndex={1} onClick={(e) => e.stopPropagation()}>
				<CustomPopover
					layoutIcon={<MoreHoriz />}
					closeOnAction
					options={[
						{
							icon: <DeleteOutline sx={{ fontSize: 18, fill: (theme) => theme.palette.error.main }} />,
							caption: (
								<Typography color={(theme) => theme.palette.error.main}>
									<FormattedMessage id={'common.delete'} />
								</Typography>
							),
							onAction: () => picture.id && deletePicture({ pictureId: picture.id }),
						},
					]}
				/>
			</Box>
			<Picture sx={{ opacity: isProcessed ? '0.7' : '1' }}>
				<Stack justifyContent={'center'} alignItems={'center'}>
					<BrokenImageOutlined />
				</Stack>
			</Picture>
		</Box>
	);
};
